/**
 * ラリーに属さない場所をプランに入れるための検索欄
 *
 * 場所の登録は呼び出し側に任せ、ここは選ばれた候補をそのまま返すだけにする。
 * Google Places は従量課金なので入力中には引かず、ボタンを押したときだけ検索する。
 */

import { Loader2, MapPin, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { searchRallyPlaces } from '@/api/services/stamp-rally'
import type { PlaceSearchResult } from '@/api/types/stamp-rally'
import { VisitedPlaceBadge } from '@/components/shared/visited-place-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatDistanceMeters } from '@/lib/activity-format-utils'
import { apiErrorMessage } from '@/lib/api-error'

type Props = {
  /** 近くの候補を上に出すための基準。無ければ全国から探す */
  origin?: { latitude: number; longitude: number } | null
  onPicked: (result: PlaceSearchResult) => void
  disabled?: boolean
}

export const SpotSearchField = ({ origin, onPicked, disabled }: Props) => {
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<PlaceSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const handleSearch = async () => {
    const query = keyword.trim()
    if (!query) return
    setIsSearching(true)
    try {
      const found = await searchRallyPlaces(query, origin)
      setResults(found)
      if (found.length === 0) toast.info('見つかりませんでした')
    } catch (error) {
      toast.error(apiErrorMessage(error, '場所の検索に失敗しました'))
    } finally {
      setIsSearching(false)
    }
  }

  const handlePick = (result: PlaceSearchResult) => {
    if (!result.google_place_id) {
      toast.error('この候補は追加できません')
      return
    }
    setResults([])
    setKeyword('')
    onPicked(result)
  }

  const busy = disabled || isSearching

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSearch()
          }}
          placeholder="店名や施設名で検索"
          maxLength={200}
        />
        <Button variant="outline" size="icon" className="shrink-0" disabled={busy} onClick={() => void handleSearch()}>
          {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="overflow-hidden rounded-lg border">
          {results.map((result) => (
            <button
              key={result.google_place_id ?? `${result.latitude},${result.longitude}`}
              disabled={disabled}
              onClick={() => handlePick(result)}
              className="flex w-full items-start gap-2 border-b border-border/50 px-3! py-2! text-left last:border-b-0"
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="min-w-0 truncate text-sm">{result.name}</p>
                  <VisitedPlaceBadge visitCount={result.visit_count} lastVisitedAt={result.last_visited_at} />
                </div>
                {result.address && <p className="truncate text-xs text-muted-foreground">{result.address}</p>}
              </div>
              {result.distance_m !== null && (
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {formatDistanceMeters(result.distance_m)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
